<script setup>
import {useNotification} from "@kyvg/vue3-notification";
import store from "@/store";
import {onMounted, onUnmounted, getCurrentInstance, defineProps, computed} from "vue";
import {log} from "@/utils/clientLogger.js";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps(['order'])

const notification = useNotification();
const instance = getCurrentInstance();

const socket = instance.appContext.config.globalProperties.$socket;

const userData = computed(() => store.getters.userData);
const userRole = computed(() => store.getters.userData?.role?.name);

const chatSyncChannel = new BroadcastChannel('chat_sync_channel');
const recentlyHandled = new Set();

chatSyncChannel.onmessage = (event) => {
  if (event.data?.type === 'HANDLED') {
    recentlyHandled.add(event.data.orderId);
    setTimeout(() => recentlyHandled.delete(event.data.orderId), 1000);
  }
};

socket.on("new-info", (data) => {
  log.info('UserNotifications:socket:new-info', {
    'userId': store.getters.userData._id,
    'data': data,
  });

  let messageText = data.text || "";
  if (data.order) {
    if (messageText) messageText += "<br>";

    let orderName = data.order.title || `${data.order.carBrand || ''} ${data.order.carModel || ''} ${data.order.carYear || ''}`.trim();

    messageText += `<strong>${t('notifications.order')}:</strong> ` + orderName + "<br>";

    if (data.order?.draftsmanId?.username) {
      messageText += `<strong>${t('notifications.draftsman')}:</strong> ` + data.order.draftsmanId.username + "<br>";
    }

    if (data.order?.clientId?.name) {
      messageText += `<strong>${t('notifications.client')}:</strong> ` + data.order.clientId.name + "<br>";
    }

    if (data.order?.managerId?.username) {
      messageText += `<strong>${t('notifications.manager')}:</strong> ` + data.order.managerId.username + "<br>";
    }
  }

  if (data.context === "all") {
    showInfo(data?.title, messageText, data.order)
    return
  }

  if (data.context === "user" && data.users.includes(store.getters.userData._id)) {
    if (data.data && (data.data.type === 'access-request' || data.data.type === 'access-response')) {
      showInfo(data?.title, messageText, data.order);
    } else {
      showWarning(data?.title, messageText, data.order);
    }
  }
});

socket.on("wide-chat", (data) => {
  const currentUserId = userData.value._id;
  const currentUserRole = userRole.value;

  const senderId = data.user?._id;

  const isPopulated = data.orderId && typeof data.orderId === 'object';
  const orderOwnerId = isPopulated ? (data.orderId.managerId?._id || data.orderId.managerId) : null;
  const orderOwnerRole = isPopulated ? data.orderId.managerId?.role?.name : null;

  let shouldShow = false;

  if (currentUserRole === 'admin') {
    shouldShow = true;
  } else if (currentUserRole === 'manager') {
    shouldShow = true;
    if (orderOwnerRole === 'distributor') {
      shouldShow = false;
    }
  } else if (currentUserRole === 'draftsman') {
    shouldShow = true;
  } else if (currentUserRole === 'distributor') {
    if (orderOwnerId === currentUserId) {
      shouldShow = true;
    }
  }

  if (senderId === currentUserId) {
    shouldShow = false;
  }

  const incomingOrderId = isPopulated ? String(data.orderId._id) : String(data.orderId);

  if (props.order !== null && props?.order === incomingOrderId) {
    shouldShow = false;
  }

  if (shouldShow) {
    setTimeout(() => {
      if (recentlyHandled.has(incomingOrderId)) {
        log.info('UserNotifications: Aborted, active tab intercepted the notification', { incomingOrderId });
        return;
      }

      const senderName = data.user?.username || data.user?.login;
      let chatText = "";

      if (data.audio) {
        chatText = `<strong>${senderName}:</strong> 🎙️ ${t('notifications.audioMessage')}`;
      } else if (data.image && data.text) {
        chatText = `<strong>${senderName}:</strong> ${data.text} <br> <img class="mw-100 w-100" src="/${data.image.path}">`;
      } else if (data.image) {
        chatText = `<strong>${senderName}:</strong> <br> <img class="mw-100 w-100" src="/${data.image.path}">`;
      } else if (data.text) {
        chatText = `<strong>${senderName}:</strong> ${data.text}`;
      }

      if (chatText) {
        const finalOrderData = isPopulated ? data.orderId : { _id: incomingOrderId };
        showChatMess(isPopulated ? data.orderId.title : null, chatText, finalOrderData);
      }
    }, 100);
  }
});

onMounted(() => {})
onUnmounted(() => {})

function showInfo(title = "", text = "", orderData = null) {
  const safeOrder = typeof orderData === 'object' && orderData !== null ? orderData : {};
  const time = new Date();
  notification.notify({
    title: title ?? t('notifications.defaultTitle'),
    text: text.target ? text.target.innerText : text,
    duration: 10000,
    group: "info",
    data: {
      time: time.toLocaleString(),
      carBrand: safeOrder.carBrand || '',
      carModel: safeOrder.carModel || '',
      carYear: safeOrder.carYear || '',
      orderId: safeOrder._id || '',
      humanId: safeOrder.humanId || ''
    }
  })
  playSound("https://assets.mixkit.co/active_storage/sfx/2870/2870.wav")
}

function showChatMess(title = "", text = "", orderData = null) {
  const safeOrder = typeof orderData === 'object' && orderData !== null ? orderData : { _id: orderData };
  const time = new Date();
  notification.notify({
    title: title ?? t('notifications.defaultTitle'),
    text: text.target ? text.target.innerText : text,
    duration: 120000,
    group: "chat",
    data: {
      time: time.toLocaleString(),
      carBrand: safeOrder.carBrand || '',
      carModel: safeOrder.carModel || '',
      carYear: safeOrder.carYear || '',
      orderId: safeOrder._id || '',
      humanId: safeOrder.humanId || ''
    }
  })
  playSound("https://assets.mixkit.co/active_storage/sfx/766/766-preview.mp3", 0.55)
}

function showWarning(title = "", text = "", orderData = null) {
  const safeOrder = typeof orderData === 'object' && orderData !== null ? orderData : {};
  const time = new Date();
  notification.notify({
    title: title ?? t('notifications.defaultTitle'),
    text: text,
    duration: 80000,
    group: "warning",
    data: {
      time: time.toLocaleString(),
      carBrand: safeOrder.carBrand || '',
      carModel: safeOrder.carModel || '',
      carYear: safeOrder.carYear || '',
      orderId: safeOrder._id || '',
      humanId: safeOrder.humanId || ''
    }
  })
  playSound("https://assets.mixkit.co/active_storage/sfx/2863/2863.wav")
}

function playSound(sound, volume = 0.33) {
  const audioContext = new AudioContext();
  audioContext.resume().then(() => {
    const audio = new Audio();
    audio.src = sound;
    audio.volume = volume;
    audio.play();
  });
}
</script>

<template>
  <div>
    <svg xmlns="http://www.w3.org/2000/svg" style="display: none;">
      <symbol id="check-circle-fill" fill="currentColor" viewBox="0 0 16 16">
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
      </symbol>
      <symbol id="info-fill" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
      </symbol>
      <symbol id="exclamation-triangle-fill" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
      </symbol>
      <symbol id="chat-fill" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M8 9h8"/>
        <path d="M8 13h6"/>
        <path d="M15 18l-3 3l-3 -3h-3a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v5.5"/>
        <path d="M19 16v3"/>
        <path d="M19 22v.01"/>
      </symbol>
    </svg>

    <notifications :max="5" group="info" position="top right">
      <template #body="props">
        <div class="alert my-notification alert-success alert-dismissible fade show pe-4 py-2">
          <p class="title mb-2">
            <svg class="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Info:">
              <use xlink:href="#info-fill"/>
            </svg>
            {{ props.item.data.carBrand }} {{ props.item.data.carModel }} {{ props.item.data.carYear }}
          </p>
          <div v-if="props.item.data.humanId || props.item.data.orderId">
            <span class="fw-normal badge p-1 alert alert-success opacity-75 mb-2">
              ID: {{ props.item.data.humanId || props.item.data.orderId.slice(-6) }}
            </span>
          </div>
          <button class="close btn-close btn" @click="props.close"></button>
          <p class="mb-1" v-html="props.item.text"/>
          <small class="pt-1" v-html="props.item.data.time"></small>
        </div>
      </template>
    </notifications>

    <notifications max="5" group="warning" position="top left">
      <template #body="props">
        <div class="alert my-notification alert-warning alert-dismissible fade show pe-0 py-2">
          <p class="title">
            <svg class="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Warning:">
              <use xlink:href="#exclamation-triangle-fill"/>
            </svg>
            {{ props.item.data.carBrand }} {{ props.item.data.carModel }} {{ props.item.data.carYear }}
          </p>
          <div v-if="props.item.data.humanId || props.item.data.orderId">
            <span class="fw-normal badge p-1 alert alert-success opacity-75 mb-2">
              ID: {{ props.item.data.humanId || props.item.data.orderId.slice(-6) }}
            </span>
          </div>
          <button class="close btn-close btn" @click="props.close"></button>
          <p class="mb-1" v-html="props.item.text"/>
          <small class="pt-1" v-html="props.item.data.time"></small>
        </div>
      </template>
    </notifications>

    <notifications max="18" group="chat" position="bottom right">
      <template #body="props">
        <div class="alert my-notification alert-dark alert-dismissible fade show pe-0 py-2">
          <p class="title mb-1">
            <svg width="24" height="24">
              <use xlink:href="#chat-fill"/>
            </svg>
            {{ props.item.data.carBrand }} {{ props.item.data.carModel }} {{ props.item.data.carYear }}
          </p>
          <div v-if="props.item.data.humanId || props.item.data.orderId">
            <span class="fw-normal badge bg-secondary opacity-75 mb-2">
              ID: {{ props.item.data.humanId || props.item.data.orderId.slice(-6) }}
            </span>
          </div>
          <button class="close btn-close btn" @click="props.close"></button>
          <p class="mb-1" v-html="props.item.text"/>
          <small class="pt-1 text-muted" v-html="props.item.data.time"></small>
        </div>
      </template>
    </notifications>
  </div>
</template>

<style lang="scss">
.my-notification {
  margin: 0 5px 5px;
  padding: 8px;
  font-size: 13px;

  .title {
    font-size: 14px; // Заголовок трохи більший за текст
    font-weight: 600;
    margin-bottom: 4px;
  }

  .badge {
    font-size: 12px; // Ще менший шрифт для ID
  }

  p {
    margin-bottom: 0;
    line-height: 1.2;
  }
}
</style>